import { SignedTermsheetAgreement } from '../../services/apiTypes_v1'

export const TERMSHEET_POLL_INTERVAL_MS = 2000

export type TermsheetStaleReason = 'amount_mismatch' | 'reinvestment_mismatch'

export type TermsheetDocumentFields = {
  status?: string
  ready?: boolean
  stale?: boolean
  staleReason?: TermsheetStaleReason
  signed?: boolean
  signedInvestmentAmount?: string | number
  currentInvestmentAmount?: string | number
  signedReinvestment?: 'Yes' | 'No'
  currentReinvestment?: 'Yes' | 'No'
  reinvestment?: string
  downloadLink?: string
  document_url?: string
}

export type TermsheetAgreementPollResult = {
  canContinue: boolean
  requiresResign: boolean
  staleReason?: TermsheetStaleReason
  resignMessage?: string
  downloadLink?: string
  status?: string
  signed?: boolean
}

type TermsheetPollResponse = {
  status?: number
  data?: SignedTermsheetAgreement
}

const extractDownloadLink = (doc: TermsheetDocumentFields | null): string | undefined => {
  if (!doc) return undefined
  return doc.downloadLink || doc.document_url || undefined
}

const parseTermsheetDocument = (doc: unknown): TermsheetDocumentFields | null => {
  if (!doc || typeof doc !== 'object') return null
  return doc as TermsheetDocumentFields
}

export const isTermsheetCancelled = (status?: string): boolean =>
  status?.toUpperCase() === 'CANCELLED'

export const isTermsheetCompleted = (status?: string): boolean =>
  status?.toUpperCase() === 'COMPLETED'

export const getTermsheetResignMessage = (
  staleReason?: TermsheetStaleReason,
  status?: string,
): string => {
  if (staleReason === 'amount_mismatch') {
    return 'Your investment amount changed. Please sign the termsheet again.'
  }
  if (staleReason === 'reinvestment_mismatch') {
    return 'Your reinvestment preference changed. Please sign the termsheet again.'
  }
  if (isTermsheetCancelled(status)) {
    return 'Your previous signature was cancelled. Please sign the term sheet again.'
  }
  return 'Your investment amount changed or the previous signature was cancelled. Please sign the term sheet again.'
}

export const termsheetRequiresResign = (fields: TermsheetDocumentFields): boolean =>
  fields.stale === true || isTermsheetCancelled(fields.status)

export const termsheetCanContinue = (fields: TermsheetDocumentFields): boolean =>
  fields.signed === true &&
  fields.stale !== true &&
  !isTermsheetCancelled(fields.status) &&
  fields.ready === true

/** Signing finished enough to close the iframe (may still wait on ready for continue). */
export const isTermsheetSigningDone = (
  result: Pick<
    TermsheetAgreementPollResult,
    'requiresResign' | 'canContinue' | 'signed' | 'status'
  >,
): boolean =>
  !result.requiresResign &&
  (result.canContinue || result.signed === true || isTermsheetCompleted(result.status))

export const parseTermsheetAgreementPollResponse = (
  response: TermsheetPollResponse | undefined,
): TermsheetAgreementPollResult => {
  const body = response?.data
  const fields = parseTermsheetDocument(body?.data)

  if (response?.status !== 200 || body?.success !== true || !fields) {
    return { canContinue: false, requiresResign: false }
  }

  const requiresResign = termsheetRequiresResign(fields)
  const canContinue = termsheetCanContinue(fields)
  const downloadLink = extractDownloadLink(fields)
  const staleReason = fields.staleReason
  const resignMessage = requiresResign
    ? getTermsheetResignMessage(staleReason, fields.status)
    : undefined

  return {
    canContinue,
    requiresResign,
    staleReason,
    resignMessage,
    downloadLink: canContinue ? downloadLink : undefined,
    status: fields.status,
    signed: fields.signed === true,
  }
}
